import React, { useCallback, useEffect, useState } from "react";

import { setFilter } from "QuotePage/Actions";
import { FAQModal } from "QuotePage/app/compactQuotePage/PopUpFAQs";
import { faqEntriesById } from "QuotePage/app/compactQuotePage/shared/FAQText";

import { ChatWidget } from "Cobalt/ChatWidget";
import type { WidgetMessage } from "Cobalt/ChatWidget";
import { useFlipper } from "Common/flipperUtils";
import { hideSalesforceChatButton, showSalesforceChatButton } from "Common/salesforceChatApi";

import {
  trackChatClosed,
  trackChatOpened,
  trackChatShown,
  trackCtaClicked,
  trackError,
  trackMessageSent,
  trackRecommendationShown,
  trackReplyReceived,
} from "./conciergeChat.analytics";
import { loadTranscript, sendMessage } from "./conciergeChat.api";
import { formatStep, formatTranscript } from "./format";
import { quotingActions } from "./quoting/catalog";

import type { ConciergeActions } from "./actions";
import type { History } from "history";
import type { Dispatch } from "redux";

interface ConciergeChatProps {
  title?: React.ReactNode;
  // Injected per mount; absent on non-SPA hosts.
  history?: History;
  dispatch?: Dispatch<any>;
  // Domain click behavior (catalog + destinations); defaults to quoting.
  actions?: ConciergeActions;
  // Flipper flag gating this mount; defaults to quoting's.
  flag?: string;
}

type GatedProps = Omit<ConciergeChatProps, "actions"> & { actions: ConciergeActions };

const CONCIERGE_FLAG = "concierge_chat";

// Builds the ChatWidget handlers and runs a feature's click effect.
function GatedConciergeChat({ title, history, dispatch, actions }: GatedProps) {
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  const { domain } = actions;

  useEffect(() => trackChatShown({ domain }), [domain]);

  // The Salesforce launcher floats in the same corner as this chat, so it steps
  // aside while this widget is mounted.
  useEffect(() => {
    hideSalesforceChatButton();
    return () => showSalesforceChatButton();
  }, []);

  const load = useCallback(
    (): Promise<WidgetMessage[]> =>
      loadTranscript(domain)
        .then((steps) => formatTranscript(actions.catalog, steps))
        .catch((err) => {
          trackError({ domain, phase: "load" });
          throw err;
        }),
    [actions, domain],
  );

  const send = useCallback(
    (text: string): Promise<WidgetMessage> => {
      trackMessageSent({ domain, messageLength: text.length });
      return sendMessage(text, domain)
        .then((step) => {
          const message = formatStep(actions.catalog, step);
          const buttons = message.buttons ?? [];
          // Parent row for the reply, then one child impression per rendered
          // recommendation — all sharing the message id as the join key.
          trackReplyReceived({ domain, messageId: message.id, replyKind: step.kind, recommendationCount: buttons.length });
          buttons.forEach((button, position) => trackRecommendationShown({ domain, messageId: message.id, feature: button.id, position }));
          return message;
        })
        .catch((err) => {
          trackError({ domain, phase: "send" });
          throw err;
        });
    },
    [actions, domain],
  );

  const onOpenChange = useCallback(
    (open: boolean, { trigger }: { trigger: "user" | "auto" }) => {
      if (open) trackChatOpened({ domain, trigger });
      else trackChatClosed({ domain, trigger });
    },
    [domain],
  );

  const onButtonClick = useCallback(
    (featureId: string, { messageId }: { messageId?: string }) => {
      const entry = actions.catalog[featureId];
      // Unknown id: a backend feature missing from the catalog — track it as a data-quality signal.
      if (!entry) {
        trackCtaClicked({ domain, feature: featureId, type: "unknown", messageId });
        return;
      }

      // Open the answer modal.
      if ("faqModal" in entry) {
        trackCtaClicked({ domain, feature: featureId, label: entry.label, type: "faq_modal", messageId });
        setOpenFaqId(featureId);
        return;
      }

      const target = actions.destinations[entry.target]();
      const applyTab = () => {
        if (entry.tab && dispatch) dispatch(setFilter("recommendation_mode", entry.tab));
      };

      // Client-side push keeps the SPA mounted; otherwise full page load.
      if (history && actions.spaRoutes.some((route) => target.spaPath.includes(route))) {
        trackCtaClicked({ domain, feature: featureId, label: entry.label, type: "nav", messageId });
        history.push(target.spaPath);
        if (entry.tab) setTimeout(applyTab, 0);
      } else {
        trackCtaClicked({ domain, feature: featureId, label: entry.label, type: "full_page", messageId });
        window.location.assign(target.absoluteUrl);
      }
    },
    [history, dispatch, actions, domain],
  );

  const faq = openFaqId ? faqEntriesById[openFaqId] : null;

  return (
    <>
      <ChatWidget title={title} load={load} send={send} onButtonClick={onButtonClick} onOpenChange={onOpenChange} />
      {faq && <FAQModal question={faq.question} answer={faq.answer} show onHide={() => setOpenFaqId(null)} />}
    </>
  );
}

// Renders children only when the given flipper flag is enabled.
function FlipperGate({ flag, children }: { flag: string; children: React.ReactNode }): React.ReactNode {
  const { flipperValues } = useFlipper(flag);
  if (!flipperValues?.[flag]) return null;
  return children;
}

export default function ConciergeChat({ actions = quotingActions, flag = CONCIERGE_FLAG, ...props }: ConciergeChatProps) {
  return (
    <FlipperGate flag={flag}>
      <GatedConciergeChat actions={actions} {...props} />
    </FlipperGate>
  );
}
